import { r as registerInstance, c as createEvent, h } from './index-BntG-4Sv.js';
import { D as DropdownItem } from './dropdown-item-WQYHXM0G.js';
import { D as DropdownItemAlignment } from './dropdown-item-alignment-DghTf3pJ.js';
import { L as LoggerProvider, T as TranslationService } from './translation.service-CyWR_ReZ.js';
import { O as OntoTooltipPlacement } from './onto-tooltip-placement-D_J2-9CG.js';
import { StartMode } from '@ontotext/workbench-api';

/**
 * Actions triggered from the Graph Explore split button UI.
 */
var GraphExploreAction;
(function (GraphExploreAction) {
    GraphExploreAction["DEFAULT"] = "default";
    GraphExploreAction["SELECT"] = "select";
    GraphExploreAction["CREATE"] = "create";
})(GraphExploreAction || (GraphExploreAction = {}));

const ontoGraphExploreSplitButtonCss = ".explore-visual-graph-button-group{display:flex;gap:1px;height:100%}.explore-visual-graph-button-group .explore-visual-graph-button{display:flex;align-items:center;gap:5px;background-color:var(--gw-button-primary-background);color:var(--gw-button-primary-color);border:none;outline:none;padding-left:1em;padding-right:1em}.explore-visual-graph-button-group .explore-visual-graph-button:hover{background-color:var(--gw-button-primary-hover-background);color:var(--gw-button-primary-hover-color);cursor:pointer}.explore-visual-graph-button-group .dropdown-wrapper{position:relative}.explore-visual-graph-button-group .explore-visual-graph-dropdown .onto-dropdown,.explore-visual-graph-button-group .explore-visual-graph-dropdown .onto-dropdown-button{background-color:var(--gw-button-primary-background);color:var(--gw-button-primary-color);height:100%}.explore-visual-graph-button-group .explore-visual-graph-dropdown .onto-dropdown-button{background-color:var(--gw-button-primary-background);color:var(--gw-button-primary-color);gap:0;padding:0 0.3em}.explore-visual-graph-button-group .explore-visual-graph-dropdown .onto-dropdown-button:hover{background-color:var(--gw-button-primary-hover-background);color:var(--gw-button-primary-hover-color)}.explore-visual-graph-button-group .explore-visual-graph-dropdown .onto-dropdown-menu-item{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:22.5rem}.explore-visual-graph-button-group .no-configurations-message{background-color:var(--gw-message-info-background);border:1px solid var(--gw-message-info-border-color);box-shadow:0 3px 6px rgba(0, 0, 0, 0.1);color:var(--gw-message-info-color);display:flex;gap:8px;justify-content:center;margin-top:1px;padding:0.5em 0.8em;position:absolute;right:0;z-index:1001}.explore-visual-graph-button-group .no-configurations-message .graph-create-link{padding:0}";

const START_MODE_IMAGE_MAP = {
    [StartMode.NODE]: 'assets/images/node-icon_32X21.png',
    [StartMode.QUERY]: 'assets/images/query-icon_32x21.png',
    [StartMode.SEARCH]: 'assets/images/search-icon_32x21.png',
};
const OntoGraphExploreSplitButton = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.graphExplore = createEvent(this, "graphExplore");
        this.logger = LoggerProvider.logger;
        /**
         * Dropdown items representing available graph configurations.
         */
        this.items = [];
        /**
         * Flag indicating whether the dropdown is open.
         */
        this.isDropdownOpen = false;
        this.handleDefaultClick = () => {
            this.emitGraphExplore();
        };
        this.handleToggle = (event) => {
            this.dropdownToggledHandler(event);
        };
        this.handleValueChanged = (event) => {
            this.emitGraphExplore(GraphExploreAction.SELECT, event.detail);
        };
        this.handleCreateClick = () => {
            this.emitGraphExplore(GraphExploreAction.CREATE);
        };
    }
    componentWillLoad() {
        this.loadGraphConfigItems();
    }
    /**
     * Handles changes to the dropdown open/close state.
     *
     * @param event - Custom event containing the dropdown state (`isOpen` flag)
     */
    dropdownToggledHandler(event) {
        this.isDropdownOpen = event.detail;
        if (this.isDropdownOpen) {
            this.loadGraphConfigItems();
        }
    }
    /**
     * Emits a graph exploration event based on the user interaction source.
     *
     * @param action - The type of exploration action:
     * - `default`: triggered by primary button click
     * - `select`: triggered by selecting a dropdown menu item
     * - `create`: triggered when navigating to create a graph view
     *
     * @param graphConfig - Optional graph configuration associated with the selected item.
     */
    emitGraphExplore(action = GraphExploreAction.DEFAULT, graphConfig) {
        this.graphExplore.emit({ action, graphConfig });
    }
    /**
     * Fetches graph configurations and maps them to dropdown items. Populates `this.items` with the result
     * or a fallback list if no data is returned or the request fails.
     */
    loadGraphConfigItems() {
        if (typeof this.fetchGraphConfigs === 'function') {
            this.fetchGraphConfigs()
                .then((configs) => {
                this.items = [...configs]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(config => new DropdownItem()
                    .setTooltip(config.name)
                    .setName(config.name)
                    .setIconImage(START_MODE_IMAGE_MAP[config.startMode])
                    .setValue(config));
            })
                .catch((error) => {
                this.items = [];
                this.logger.error('Failed to load graph configurations ', error);
            });
        }
    }
    render() {
        return (h("div", { key: '135ed285048beab4ae0a4ccfa4a37bfa53a88362', class: 'explore-visual-graph-button-group' }, h("button", { key: '013e29e4557b5b87fbfae87e34969df1e84168a1', class: 'explore-visual-graph-button icon-data', onClick: this.handleDefaultClick, "guide-selector": 'explore-visual', "tooltip-content": TranslationService.translate('graph_explore_split_button.buttons.explore_visual_graph.tooltip'), "tooltip-placement": OntoTooltipPlacement.TOP }, this.label), h("div", { key: 'ec88b2c64867c0b54e5922dc54bd6aba644eee70', class: 'dropdown-wrapper' }, h("onto-dropdown", { key: '40120ab15300550c540c44bd851112fbd07c6d04', class: 'explore-visual-graph-dropdown', autoClose: true, items: this.items, onToggle: this.handleToggle, onValueChanged: this.handleValueChanged, dropdownAlignment: DropdownItemAlignment.RIGHT, tooltipPlacement: OntoTooltipPlacement.TOP, dropdownButtonTooltip: TranslationService.translate('graph_explore_split_button.buttons.explore_visual_graph_dropdown.tooltip') }), this.isDropdownOpen && this.items.length === 0 ? (h("div", { class: "no-configurations-message" }, h("i", { class: "icon-info" }), h("div", { class: "message-text" }, h("div", null, h("translate-label", { labelKey: 'graph_explore_split_button.messages.missing_graph_configuration' })), h("div", null, h("translate-label", { labelKey: 'graph_explore_split_button.messages.create_graph_configuration' }), "\u00A0", h("button", { class: 'btn btn-link graph-create-link', onClick: this.handleCreateClick }, h("translate-label", { labelKey: 'graph_explore_split_button.messages.create_graph_configuration_link' })))))) : '')));
    }
};
OntoGraphExploreSplitButton.style = ontoGraphExploreSplitButtonCss;

export { OntoGraphExploreSplitButton as onto_graph_explore_split_button };
